import { cn } from '@/lib/cn'
import type { GameInfo } from '@/models/game-info'
import { SkeletonLine } from '../ui/Skeleton'

interface ValueBadgeProps {
  gameInfo: GameInfo | null
  className?: string
  /** `stacked` puts FREE under the struck price; `inline` puts it beside. */
  layout?: 'stacked' | 'inline'
}

/**
 * What the prize is worth, and that it costs nothing.
 *
 * The Quasar card showed the game's price in a solid gold chip, which reads as
 * a price tag — as though the giveaway were a purchase. The value is struck
 * through and demoted to a caption here, and the gold now carries "Free", so
 * the chip states the offer instead of a price.
 */
export function ValueBadge({ gameInfo, className, layout = 'stacked' }: ValueBadgeProps) {
  const price = gameInfo?.display_price
  if (!price) return null

  return (
    <div className={cn('value-badge', `is-${layout}`, className)}>
      <span className="value-badge-price">
        <span className="sr-only">Normally </span>
        {price}
      </span>
      <span className="value-badge-free">Free</span>
    </div>
  )
}

/** The badge's shape while the giveaway loads: price line and FREE pill. */
export function ValueBadgeSkeleton({
  className,
  layout = 'stacked',
}: Omit<ValueBadgeProps, 'gameInfo'>) {
  return (
    <div className={cn('value-badge', `is-${layout}`, className)} aria-hidden="true">
      <span className="value-badge-price">
        <SkeletonLine width="3.2em" />
      </span>
      <span className="value-badge-free skeleton skeleton-ghost">Free</span>
    </div>
  )
}
